<template>
    <div>
        <v-row>
            <v-col cols="12">
                <v-row class="align-center mx-6">
                    <div class="title">{{ $t('Agreements') }}</div>
                    <dropdown
                        v-if="flextimeCompanies.length > 1"
                        :items="flextimeCompanies"
                        :selected="flextimeCompanies.filter(x => x.id === selectedCompanyId)"
                        :avatars="false"
                        append-icon="work"
                        name-key="name"
                        value-key="id"
                        show-selected-first
                        @input="onSelectCompanyAccount"
                    >
                        <template #default="{ on }">
                            <FilterSelectButton
                                :on="on"
                                icon="Briefcase"
                                show-label
                                full-width
                                custom-style="margin: 0px 20px 0px 20px"
                                :label="$t('Account') + ': ' + flextimeCompanies.find(x => x.id === selectedCompanyId).name"
                            />
                        </template>
                    </dropdown>
                    <FilterSelectButton
                        v-else
                        icon="Briefcase"
                        show-label
                        full-width
                        label-view
                        custom-style="margin: 0px 20px 0px 20px; cursor: default;"
                        :label="$t('Account') + ': ' + flextimeCompanies.find(x => x.id === selectedCompanyId).name"
                    />
                    <v-spacer />
                    <span v-if="canEditCompany">
                        <primary-button color="primary" @click="isAgreementsModalVisible = true">
                            {{ $t('Edit agreements') }}
                        </primary-button>
                    </span>
                </v-row>
            </v-col>
        </v-row>
        <v-divider class="mb-2"/>
        <v-row v-if="content">
            <v-col
                cols="12"
                class="pb-5 pt-2"
            >
                <v-row
                    no-gutters
                    align="baseline"
                    justify="space-between"
                    class="pr-2"
                    v-if="content.title"
                >
                    <v-col cols="3" class="title">
                        {{ content.title }}
                    </v-col>
                </v-row>
                <v-row
                    v-for="(row, idx) in content.rows"
                    :key="'r' + idx"
                    align="center"
                    no-gutters
                    class="my-3"
                >
                    <template>
                        <v-col
                            cols="3"
                            class="body-2 grey--text text--darken-1 text-sm-left"
                        >
                            {{ row.label }}
                        </v-col>
                        <v-col
                            class="text-sm-left"
                        >
                            <div>
                                {{ row.content }}
                            </div>
                            <div
                                v-if="row.description"
                                class="body-2 grey--text text--darken-1 text-sm-left"
                            >
                                {{ row.description }}
                            </div>
                        </v-col>
                    </template>
                </v-row>
            </v-col>
        </v-row>
        <AgreementsModal
            v-if="isAgreementsModalVisible"
            :person-id="personId"
            :person-name="`${profile.person_settings.first_name ? profile.person_settings.first_name : ''} ${profile.person_settings.surname ? profile.person_settings.surname : ''}`"
            :company-id="selectedCompanyId"
            @close="isAgreementsModalVisible = false"
            @update="fetchFlextimeSettings"
        />
    </div>
</template>

<script setup>
import {computed, ref, onMounted, onBeforeMount} from "@vue/composition-api";
import AgreementsModal from './AgreementsModal.vue'
import ApiProfile from "@/api/profile/profile"
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {formatDate, formatTimeDuration, parseSysDate} from "@/plugins/formatting";
import RightsModule from "@/composables/use-rights";
import {$t} from '../../../i18n'
import FilterSelectButton from "@/components/ModernFilterBar/components/FilterSelectButton.vue";
import {Router} from "@/composables/Router";
import AccountModule from '@/store/modules/account';
import {getCookie} from '../../../helpers/helpers'

export default {
    name: "ProfileAgreements",
    components: {
        AgreementsModal,
        FilterSelectButton
    },
    props: {
        personId: {
            type: Number,
            default: 0
        },
        profile: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const router = Router();
        const {can_edit_account} = RightsModule();

        const isAgreementsModalVisible = ref(false)
        const flexTimeSettings = ref(null);
        const companyFlexTimeSettings = ref(null);
        const {current_account} = AccountModule()
        const defaultCompanyId = current_account.value?.data?.default_account_id ?? parseInt(getCookie("account_id"))

        const flextimeCompanies = computed(() => props.profile.companies.filter(x => x.flextime_active))

        const selectedCompanyId = ref(flextimeCompanies.value.length ? flextimeCompanies.value[0].id : null);
        if (router.currentRoute.params.companyId) {
            selectedCompanyId.value = router.currentRoute.params.companyId
        } else if(flextimeCompanies.value.findIndex((x) => x.id === defaultCompanyId) !== -1) {
            selectedCompanyId.value = defaultCompanyId
        }
        const canEditCompany = computed(() => can_edit_account(selectedCompanyId.value))

        const formatPeriod = (item) => {
            const startDate = item.settings_owner_date_from ? formatDate(parseSysDate(item.settings_owner_date_from)) : '...'
            const endDate = item.settings_owner_date_until ? formatDate(parseSysDate(item.settings_owner_date_until)) : '...'
            let type = $t('System')

            if (item.settings_owner_type === 'settings_owner_type_company') {
                type = $t('Company')
            } else if (item.settings_owner_type === 'settings_owner_type_person') {
                type = $t('Person')
            }

            if (!item.settings_owner_date_from && !item.settings_owner_date_until) {
                return type
            }

            return `${type} ${startDate} - ${endDate}`
        }

        const getTotalWorkingHourDuration = (item) => {
            if (!item._data) {
                return 0
            }
            let total = 0
            total += item._data.monday
            total += item._data.tuesday
            total += item._data.wednesday
            total += item._data.thursday
            total += item._data.friday
            total += item._data.saturday
            total += item._data.sunday
            return formatTimeDuration(total, '', true, true)
        }

        const content = computed(() => {
            return flexTimeSettings.value ? {
                rows: [
                    {
                        label: $t('Active agreement'),
                        content: formatPeriod(flexTimeSettings.value)
                    },
                    {
                        label: $t('Weekly hours'),
                        content: getTotalWorkingHourDuration(flexTimeSettings.value)
                    },
                    {
                        label: $t('Work on holidays'),
                        content: flexTimeSettings.value.work_holidays ? $t('Yes') : $t('No')
                    },
                ]
            } : null
        })

        const fetchFlextimeSettings = async () => {
            if (!selectedCompanyId.value) {
                return
            }

            try {
                const data = await ApiProfile.getProfileFlextimeSettings(selectedCompanyId.value, props.personId)
                flexTimeSettings.value = data.person_flextime_settings
                companyFlexTimeSettings.value = data.company_flextime_settings
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        onMounted(async () => {
            try {
                await fetchFlextimeSettings()
            } catch (e) {
                errorDialog.showError(e)
            }
        })

        onBeforeMount(() => {
            if(flextimeCompanies.value.length < 1) {
                router.replace({name: "profile_settings"})
                return
            }
        })

        const onSelectCompanyAccount = (company) => {
            selectedCompanyId.value = company.id
            fetchFlextimeSettings()
        }

        return {
            flextimeCompanies,
            selectedCompanyId,
            isAgreementsModalVisible,
            canEditCompany,
            onSelectCompanyAccount,
            fetchFlextimeSettings,
            content
        }
    }
}
</script>

<style scoped lang="scss">

</style>